import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { IdCardIcon, PencilIcon, SearchIcon, TrashIcon } from '../assets/Icon';
import AddUserModal from '../components/dialogs/add-user-dialog';
import DeleteUserModal from '../components/dialogs/delete-user-dialog';
import TableComponent from '../components/Table';
import CustomDateRangePicker from '../components/common/DatePicker/CustomDateRangePicker';

import useAPI from '../hooks/useAPI';
import { dateFormate, toUpperCase } from '../lib/constant';
import { UsersEntity } from '../lib/types/UserManagement';
import AuthContext from '../contexts/AuthContext';
import styles from './UsersManagement.module.scss';
import { convertToCSV } from '../lib/lib';
import apiInstance from '../config/axios';
// import apiInstance from '../config/axios';

interface Option {
  label: string;
  value: string;
}

interface IPaginationObject {
  limit: number;
  page: number;
  search: string;
  status: string;
  role: string;
  from: string;
  to: string;
}

interface IUsersResponse {
  result: {
    users: UsersEntity[];
    totalUsers: number;
    totalPages: number;
  };
}

const RoleOptions: Option[] = [
  { label: 'Organization', value: 'ORGANIZATION' },
  { label: 'Vendor', value: 'VENDOR' },
  { label: 'Employee', value: 'EMPLOYEE' },
];

const StatusOptions: Option[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Pending', value: 'PENDING' },
];

const MAX_DATE_RANGE_DAYS = 90;

export default function UsersManagement() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isDelete, setIsDelete] = useState<null | UsersEntity>(null);
  const [edited, setEdited] = useState<null | UsersEntity>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Pagination and filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    role: '',
    from: '',
    to: '',
  });

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Search debounce
  const [searchInput, setSearchInput] = useState('');

  const { userData: selfData } = useContext(AuthContext);

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    params.append('role', paginationData.role);
    params.append('status', paginationData.status);
    if (paginationData.search) {
      params.append('search', paginationData.search);
    }
    if (paginationData.from) {
      params.append('fromDate', paginationData.from);
    }
    if (paginationData.to) {
      params.append('toDate', paginationData.to);
    }
    return `/auth/users/list?${params.toString()}`;
  };

  // Fetch users using useAPI
  const {
    data: usersResponse,
    refetch,
    isLoading,
  } = useAPI<void, IUsersResponse>({
    url: buildApiUrl(),
    method: 'GET',
    dependencies: [
      paginationData.page,
      paginationData.limit,
      paginationData.role,
      paginationData.status,
      paginationData.search,
      paginationData.from,
      paginationData.to,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      if (paginationData.status || paginationData.role) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error('Network error. Please check your connection and retry.');
      }
    },
  });

  const usersData = usersResponse?.result?.users || [];
  const totalCount = usersResponse?.result?.totalUsers || 0;

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== paginationData.search) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          search: searchInput,
        }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'all' ? '' : value,
    }));
  };

  // Handle role filter change
  const handleRoleChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      role: value === 'all' ? '' : value,
    }));
  };

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      search: '',
      status: '',
      role: '',
      from: '',
      to: '',
    });
  };
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', paginationData.status || '');
      params.append('fromDate', paginationData.from);
      params.append('toDate', paginationData.to);
      params.append('role', paginationData.role || '');
      params.append('search', paginationData.search || '');

      const response = await apiInstance.get(
        `/auth/users/download?${params.toString()}`,
      );

      const transactionHistory = response.data.result.users;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `UserManagement${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = (row: UsersEntity) => {
    if (row?.status === 'PENDING') {
      return toast.error('Cannot delete user with pending status.');
    } else if (
      row?.role === 'ORGANIZATION' &&
      selfData?.role == 'ORGANIZATION'
    ) {
      return toast.error('Cannot delete user with organization role.');
    } else {
      return setIsDelete(row);
    }
  };

  const columns = [
    {
      label: 'First Name',
      id: 'firstName',
      width: 100,
      renderCell: (row: any) => (
        <div style={{ whiteSpace: 'nowrap' }}>{row.firstName || '-'}</div>
      ),
    },
    {
      label: 'Last Name',
      id: 'lastName',
      width: 100,
      renderCell: (row: any) => (
        <div style={{ whiteSpace: 'nowrap' }}>{row.lastName || '-'}</div>
      ),
    },
    {
      label: 'Email',
      id: 'email',
      width: 100,
      renderCell: (row: any) => (
        <div style={{ whiteSpace: 'nowrap' }}>{row.email || '-'}</div>
      ),
    },
    {
      label: 'Role',
      id: 'role',
      renderCell: (row: any) => <div>{toUpperCase(row.role)}</div>,
    },
    {
      label: 'Is New User',
      id: 'isNewUser',
      renderCell: (row: any) => <div>{row.isNewUser ? 'Yes' : 'No'}</div>,
    },
    {
      label: 'Wallet Address',
      id: 'walletAddress',
      renderCell: (row: any) => (
        <div>{row.wallet?.[0]?.walletAddress || '-'}</div>
      ),
    },
    {
      label: '# of Available Tokens',
      id: 'numberOfToken',
      renderCell: (row: any) => <div>{row.numberOfToken ?? '-'}</div>,
    },
    {
      label: 'Status',
      id: 'status',
      renderCell: (row: any) => <div>{toUpperCase(row.status)}</div>,
    },
    {
      label: 'Registered Date',
      id: 'createdAt',
      renderCell: (row: any) =>
        row.createdAt ? dateFormate(row.createdAt) : '-',
    },
    {
      label: 'Action',
      id: 'id',
      renderCell: (row: any) => (
        <div className="d-flex align-items-center gap-2">
          {row?.status === 'PENDING' && (
            <Button
              variant="plain"
              className="px-0 outline-0"
              onClick={() => setEdited(row)}
            >
              <PencilIcon color="#666666" />
            </Button>
          )}
          <Button
            onClick={() => handleDelete(row)}
            variant="plain"
            className="px-0"
          >
            <TrashIcon color="#666666" />
          </Button>
          <Link to={`/user-management/details?id=${row.id}`}>
            <IdCardIcon color="#666666" />
          </Link>
        </div>
      ),
    },
  ];

  const handelClose = () => {
    setOpen(false);
    setEdited(null);
    setPaginationData(prev => ({ ...prev, page: 1 }));
    refetch();
  };

  useEffect(() => {
    refetch();
  }, [location.state?.lastRefreshedAt]);

  const hasActiveFilters =
    paginationData.search ||
    paginationData.status ||
    paginationData.role ||
    paginationData.from ||
    paginationData.to;

  return (
    <>
      <div
        className={`d-flex flex-wrap justify-content-between mb-3 ${styles.headerContainer}`}
      >
        <div className={styles.titleContainer}>
          <h2 className="fw-bold mb-0">User Management</h2>
        </div>
        <div
          className={`d-flex justify-content-end ${styles.filtersContainer}`}
        >
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Form.Group className="mb-0">
              <Button
                type="button"
                onClick={() => setOpen(true)}
                variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
              >
                Add User
              </Button>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3 p-3">
        {/* Filters Section */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-start">
          {/* Search Input */}
          <div
            className="flex-grow-1"
            style={{ minWidth: '200px', maxWidth: '300px' }}
          >
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-white">
                <SearchIcon size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, Email, Wallet address"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="border-start-0 custom-input"
              />
            </InputGroup>
          </div>

          {/* Date Range Picker */}
          <div style={{ minWidth: '220px' }} className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">
              Registered Date <span className="text-muted fw-normal"></span>
            </Form.Label>
            <CustomDateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              // @ts-ignore
              setEndDate={handleEndDateChange}
              placeholder="Select date range"
            />
            {dateError && (
              <Form.Text className="text-danger small">{dateError}</Form.Text>
            )}
          </div>

          {/* Role Filter */}
          <div style={{ minWidth: '150px' }}>
            <Form.Label className="small fw-semibold mb-1">Role</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              value={paginationData.role || 'all'}
              onChange={e => handleRoleChange(e.target.value)}
            >
              <option value="all">All Roles</option>
              {RoleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '150px' }}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              value={paginationData.status || 'all'}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="all">All Status</option>
              {StatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1 custom-filter">
                {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
              </Form.Label>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearFilters}
                className="rounded-2 custom-filter-button"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Download Button */}
          <div className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
              className="rounded-2 custom-filter-button"
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <div className={styles.tableContainer}>
          <TableComponent
            data={usersData}
            columns={columns}
            totalCount={totalCount}
            fetchMore={handleFetchMoreData}
            isServerSidePagination
            perPageSize={paginationData.limit}
            activePage={paginationData.page}
            loading={isLoading}
            customBodyCss="custom-table-users"
          />
        </div>
      </div>

      <AddUserModal
        open={open || !!edited}
        onClose={handelClose}
        editData={edited}
        setEditData={setEdited}
        refetch={refetch}
      />
      <DeleteUserModal
        open={!!isDelete}
        onClose={() => {
          setIsDelete(null);
          refetch();
        }}
        data={isDelete}
      />
    </>
  );
}
